import { type MouseEvent, Fragment } from "react";

import { NamePart } from "@/src/components/cards/patient-card/patient-card-header";
import { CopyableContent } from "@/src/components/copyable-content";
import { TooltipProvider } from "@/src/components/shadcn/ui/tooltip";
import { ValerieCardField } from "@/src/components/valerie-card";
import { ComparisonLabel, useComparison } from "@/src/context/comparison-text";
import { properCase } from "@/src/lib/string";
import { Address, AddressParts, getFormattedAddress } from "@/src/models/address";

const ADDRESS_PARTS: { key: keyof AddressParts; label: string; format: (value: string) => string }[] = [
  { key: "line1", label: "address", format: properCase },
  { key: "line2", label: "address 2", format: properCase },
  { key: "city", label: "city", format: properCase },
  { key: "state", label: "state", format: (value) => value.toUpperCase() },
  { key: "postalCode", label: "zip code", format: (value) => value },
];

async function copyPart(event: MouseEvent, text: string) {
  event.stopPropagation();
  try {
    await navigator.clipboard.writeText(text);
  } catch {}
}

export function PatientAddressField({
  address,
  secondaryAddress,
  hasConflict,
}: {
  address: Address | null;
  secondaryAddress: Address | null;
  hasConflict: boolean;
}) {
  const comparisonLabel = useComparison()?.comparisonLabel ?? ComparisonLabel.EMR;
  const fullAddress = getFormattedAddress(address);
  const parts = ADDRESS_PARTS.flatMap((part) => {
    const value = address?.[part.key]?.trim();

    return value ? [{ ...part, value: part.format(value) }] : [];
  });

  return (
    <ValerieCardField label="Address" colSpan={3}>
      {fullAddress ? (
        <TooltipProvider>
          <CopyableContent text={fullAddress} textClassName={hasConflict ? "bg-warn/20" : "text-foreground"}>
            {parts.map((part, index) => (
              <Fragment key={part.key}>
                {index > 0 ? (parts[index - 1].key === "city" ? ", " : " ") : null}
                <NamePart value={part.value} display={part.value} label={part.label} onCopy={copyPart} />
              </Fragment>
            ))}
          </CopyableContent>
        </TooltipProvider>
      ) : (
        <span className="text-sm text-muted-foreground/60">—</span>
      )}
      {hasConflict && (
        <div className="mt-1 text-xs text-muted-foreground">
          <span className="text-warn dark:text-warn">
            {comparisonLabel}:{" "}
            <CopyableContent
              text={getFormattedAddress(secondaryAddress) ?? "N/A"}
              textClassName="text-warn dark:text-warn"
            />
          </span>
        </div>
      )}
    </ValerieCardField>
  );
}
